/**
 * Wire-format transform. Port of `obd_frontend/Helpers/utils.transformNodes`
 * and `validateIVR`. The output JSON must be byte-identical to the legacy
 * so the Asterisk dialplan keeps working.
 *
 * Read [./types.ts] for the canonical wire shape. Do NOT change field names
 * here — they're consumed by the backend.
 */
import { toast } from 'sonner';
import {
  DTMF_KEYS,
  type IvrEdge,
  type IvrFlowControl,
  type IvrFlowControlNext,
  type IvrFlowJson,
  type IvrNode,
  type IvrScheduleData,
  type IvrTransformedNode,
  type IvrWireData,
} from './types';

const getDtmfIndex = (edge: IvrEdge): number => {
  const key = edge.sourceHandle?.split('-').pop();
  if (!key) return Number.MAX_SAFE_INTEGER;
  const idx = DTMF_KEYS.indexOf(key as (typeof DTMF_KEYS)[number]);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

/**
 * Sum durations along the LEFT-MOST path so the campaign engine can
 * estimate per-call wall time. Visit-set guards against cycles. The
 * algorithm intentionally adds the current node's duration BEFORE
 * recursing (matches legacy).
 */
function calculateLeftPathDuration(
  node: IvrNode | undefined,
  nodesArray: IvrNode[],
  edges: IvrEdge[],
  visited: Set<string> = new Set(),
): number {
  if (!node) return 0;
  let duration = 0;
  const verb = node.data?.verb;

  if (verb === 'play' || verb === 'record') {
    const d = (node.data?.action as { duration?: number } | undefined)?.duration;
    duration += Number(d ?? 0);
  } else if (verb === 'dtmf' || verb === 'gather') {
    duration += Number(node.data?.timeout ?? 0);
    const menuDuration = (
      node.data?.action as { menu?: { duration?: number } } | undefined
    )?.menu?.duration;
    duration += Number(menuDuration ?? 0);
  }

  if (visited.has(node.id)) return duration;
  visited.add(node.id);

  const edgeData = edges.filter((e) => e.source === node.id);

  if (verb === 'dtmf') {
    const sorted = [...edgeData].sort(
      (a, b) => getDtmfIndex(a) - getDtmfIndex(b),
    );
    const firstEdge = sorted[0];
    if (firstEdge) {
      const target = nodesArray.find((n) => n.id === firstEdge.target);
      duration += calculateLeftPathDuration(target, nodesArray, edges, visited);
    }
  } else if (edgeData.length > 0) {
    const firstEdge = edgeData[0]!;
    const target = nodesArray.find((n) => n.id === firstEdge.target);
    duration += calculateLeftPathDuration(target, nodesArray, edges, visited);
  }

  return duration;
}

export interface TransformArgs {
  nodesArray: IvrNode[];
  edges: IvrEdge[];
  flowName: string;
  scheduleData?: IvrScheduleData;
  isGatherNode: number;
  userId: number | string;
  smsWebhookFlag: boolean;
}

/** Produce the wire `IvrWireData` shape. */
export function transformNodes(args: TransformArgs): IvrWireData {
  const {
    nodesArray,
    edges,
    flowName,
    scheduleData,
    isGatherNode,
    userId,
    smsWebhookFlag,
  } = args;

  const nodeIdMap = new Map<string, number>(
    nodesArray.map((node, index) => [node.id, index]),
  );

  const totalDuration = calculateLeftPathDuration(
    nodesArray[0],
    nodesArray,
    edges,
  );

  const transformedNodes: Record<number, IvrTransformedNode> = {};

  for (const node of nodesArray) {
    const edgeData = edges.filter((e) => e.source === node.id);
    const nodeId = nodeIdMap.get(node.id);
    if (nodeId === undefined) continue;

    const verb = node.data?.verb;
    const flowJson: IvrFlowJson = { verb };

    if (verb === 'play') {
      flowJson.action = node.data?.action;
      flowJson.duration =
        Number((node.data?.action as { duration?: number } | undefined)?.duration ?? 0) || 0;
    } else if (verb === 'record') {
      flowJson.action = node.data?.action;
      flowJson.duration =
        Number((node.data?.action as { duration?: number } | undefined)?.duration ?? 0) || 0;
      flowJson.record_duration = node.data?.record_duration;
    } else if (verb === 'dtmf' || verb === 'gather') {
      flowJson.type = node.data?.type;
      flowJson.action = node.data?.action;
      flowJson.numDigits = node.data?.numDigits;
      flowJson.timeout = node.data?.timeout;
      flowJson.numSources = node.data?.numSources;
      flowJson.rePrompt = node.data?.rePrompt;
      flowJson.ttsRows = node.data?.ttsRows ?? [];
      flowJson.minValue = node.data?.minValue ?? '';
      flowJson.manualDigits = node.data?.manualDigits ?? '';
      flowJson.smsData = node.data?.smsData ?? {};
    } else if (verb === 'transfer') {
      flowJson.action = node.data?.action;
    }

    const flowControl: IvrFlowControl = {};

    if (
      verb === 'dtmf' &&
      Object.keys(node.data?.selectedHandles ?? {}).length !== 0
    ) {
      const sorted = [...edgeData].sort(
        (a, b) => getDtmfIndex(a) - getDtmfIndex(b),
      );
      for (const edge of sorted) {
        const key = edge.sourceHandle?.split('-').pop();
        const targetNumericId = nodeIdMap.get(edge.target);
        if (!key || targetNumericId === undefined) continue;
        const next: IvrFlowControlNext = {
          flowName,
          nextNodeId: targetNumericId,
          isLoop: edge.source === edge.target,
          parent: node.data?.parent ?? '',
        };
        flowControl[`dtmf_${key}`] = next;
      }
    } else if (edgeData.length > 0) {
      const targetNumericId = nodeIdMap.get(edgeData[0]!.target);
      if (targetNumericId !== undefined) {
        flowControl.default = {
          flowName,
          nextNodeId: targetNumericId,
          parent: node.data?.parent ?? '',
        };
      }
    }

    transformedNodes[nodeId] = {
      id: nodeId,
      flowJson,
      flowRender: { ...node },
      flowControl,
    };
  }

  return {
    flowName,
    userId,
    nodes: transformedNodes,
    scheduleData,
    isGatherNode,
    promptDuration: totalDuration,
    smsWebhookFlag,
  };
}

/* ---------------- Validation ---------------- */

export interface ValidateArgs {
  nodes: IvrNode[];
  edges: IvrEdge[];
  flowName: string;
}

/**
 * Mirrors legacy `validateIVR`. Returns true iff the IVR is shippable;
 * pops toasts on every failure. Designed for the Save button click.
 */
export function validateIVR({ nodes, edges, flowName }: ValidateArgs): boolean {
  if (!nodes.length) {
    toast.error('Add at least one node.');
    return false;
  }
  if (!flowName.trim()) {
    toast.error('IVR Name is required.');
    return false;
  }

  const incomingMap = new Map<string, IvrEdge[]>();
  const outgoingMap = new Map<string, IvrEdge[]>();
  for (const edge of edges) {
    const incoming = incomingMap.get(edge.target) ?? [];
    incoming.push(edge);
    incomingMap.set(edge.target, incoming);
    const outgoing = outgoingMap.get(edge.source) ?? [];
    outgoing.push(edge);
    outgoingMap.set(edge.source, outgoing);
  }

  for (const node of nodes) {
    const incoming = incomingMap.get(node.id) ?? [];
    const outgoing = outgoingMap.get(node.id) ?? [];
    const label = String(node.data?.label ?? '').toUpperCase();

    // Rule 1: input handle must have at least one connection (except answer nodes).
    if (
      (node.data as { hasInput?: boolean } | undefined)?.hasInput &&
      node.type !== 'answerNode' &&
      incoming.length === 0
    ) {
      toast.error(`Node "${label}" must have at least one input connected.`);
      return false;
    }

    // Rule 2: selected output handles must be connected.
    const selectedHandles = node.data?.selectedHandles;
    if (selectedHandles) {
      for (const [handleId, handleObj] of Object.entries(selectedHandles)) {
        if (!handleObj || Object.keys(handleObj).length === 0) continue;
        const isConnected = outgoing.some((e) => e.sourceHandle === handleId);
        if (!isConnected) {
          const dtmf = handleId.split('-')[1] ?? '?';
          toast.error(
            `Output handle "DTMF_${dtmf}" of node "${label}" must be connected.`,
          );
          return false;
        }
      }
    }

    // Rule 3: end nodes must be Hangup nodes.
    if (outgoing.length === 0 && node.type !== 'hangupNode') {
      toast.error(`End node "${label}" must be a Hangup node.`);
      return false;
    }

    // Rule 4: Play node must have an audio file.
    if (node.type === 'playNode') {
      const action = node.data?.action as { audio?: string } | undefined;
      if (!action?.audio) {
        toast.error(`Node "${label}" must select an audio file.`);
        return false;
      }
    }

    // Rule 5: Record node must have audio + valid record_duration.
    if (node.type === 'recordNode') {
      const action = node.data?.action as { audio?: string } | undefined;
      const dur = node.data?.record_duration;
      if (!action?.audio) {
        toast.error(`Node "${label}" must select an audio file.`);
        return false;
      }
      const n = Number(dur);
      if (!dur || !Number.isInteger(n) || n <= 0) {
        toast.error(
          `Node "${label}" must have a valid record duration (positive integer).`,
        );
        return false;
      }
    }

    // Rule 6: Gather node must have menu audio.
if (node.type === 'gatherNode') {
  const action = node.data?.action as
    | { menu?: { audio?: string } }
    | undefined;
  if (!action?.menu?.audio) {
    toast.error(`Node "${label}" must select an audio file.`);
    return false;
  }

  // Rule 6a: wait time is required, 1-15 seconds.
  const timeout = Number(node.data?.timeout);
  if (!Number.isInteger(timeout) || timeout < 1 || timeout > 15) {
    toast.error(
      `Node "${label}" wait time must be between 1 and 15 seconds.`,
    );
    return false;
  }

  // Rule 6b: re-prompt count is optional, but if set must be 1-5.
  const rePromptRaw = node.data?.rePrompt;
  if (rePromptRaw !== '' && rePromptRaw !== undefined && rePromptRaw !== null) {
    const rePrompt = Number(rePromptRaw);
    if (!Number.isInteger(rePrompt) || rePrompt < 1 || rePrompt > 5) {
      toast.error(
        `Node "${label}" re-prompt count must be between 1 and 5.`,
      );
      return false;
    }
  }
}
  }

  return true;
}
